<template>
    <div class="zs-tree-container" :class="{ 'has-border': border }">
        <div v-if="title" class="zs-tree-header">
            {{ title }}
        </div>
        <ul class="zs-tree">
            <ZSTreeItem
                v-for="node in data"
                ref="treeItemRefs"
                :key="node[itemKey]"
                :node="node"
                :item-key="itemKey"
                :item-label="itemLabel"
                :item-value="itemValue"
                :item-children="itemChildren"
                :selectable="selectable"
                :is-selectable="isSelectable"
                :multiselect="multiselect"
                :model-value="modelValue"
                :show-checkboxes="showCheckboxes"
                :show-icons="showIcons"
                :node-icon="nodeIcon"
                :node-icon-color="nodeIconColor"
                :expander-icon="expanderIcon"
                :depth="0"
                :initially-expanded="initiallyExpanded"
                @update:model-value="$emit('update:modelValue', $event)"
                @node-click="$emit('node-click', $event)"
                @node-expand="$emit('node-expand', $event)"
            >
                <template #default="slotProps">
                    <slot v-bind="slotProps"></slot>
                </template>
                <template #expander="slotProps">
                    <slot name="expander" v-bind="slotProps" />
                </template>
                <template #icon="slotProps">
                    <slot name="icon" v-bind="slotProps" />
                </template>
            </ZSTreeItem>
        </ul>
        <div v-if="!data || !data.length" class="zs-tree-empty">
            <slot name="empty">
                {{ emptyFallback }}
            </slot>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ZSTreeItem from './ZSTreeItem.vue';

const { t } = useI18n();

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    itemKey: {
        type: String,
        default: 'id'
    },
    itemLabel: {
        type: [String, Function],
        default: 'label'
    },
    itemValue: {
        type: [String, Function],
        default: 'id'
    },
    itemChildren: {
        type: String,
        default: 'children'
    },
    selectable: {
        type: Boolean,
        default: false
    },
    isSelectable: {
        type: Function,
        default: null
    },
    multiselect: {
        type: Boolean,
        default: false
    },
    modelValue: {
        type: [Object, String, Number, Array],
        default: null
    },
    showCheckboxes: {
        type: Boolean,
        default: false
    },
    showIcons: {
        type: Boolean,
        default: false
    },
    nodeIcon: {
        type: [String, Function],
        default: ''
    },
    nodeIconColor: {
        type: String,
        default: ''
    },
    expanderIcon: {
        type: String,
        default: 'Chevron'
    },
    initiallyExpanded: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    border: {
        type: Boolean,
        default: false
    },
    emptyText: {
        type: String,
        default: ''
    }
});

defineEmits(['update:modelValue', 'node-click', 'node-expand']);

const treeItemRefs = ref([]);

const emptyFallback = computed(() => props.emptyText || t('components.no_data'));

const expandAllNodes = (nodeRefs) => {
    if (!Array.isArray(nodeRefs)) {
        return;
    }

    nodeRefs.forEach((nodeRef) => {
        if (!nodeRef) {
            return;
        }

        nodeRef.expandNode();

        const childRefs = nodeRef.$refs.childItems;
        if (childRefs) {
            expandAllNodes(childRefs);
        }
    });
};

const collapseAllNodes = (nodeRefs) => {
    if (!Array.isArray(nodeRefs)) {
        return;
    }

    nodeRefs.forEach((nodeRef) => {
        if (!nodeRef) {
            return;
        }

        nodeRef.collapseNode();

        const childRefs = nodeRef.$refs.childItems;
        if (childRefs) {
            collapseAllNodes(childRefs);
        }
    });
};

const expandAll = () => {
    expandAllNodes(treeItemRefs.value);
};

const collapseAll = () => {
    collapseAllNodes(treeItemRefs.value);
};

defineExpose({
    expandAll,
    collapseAll
});
</script>

<style scoped>
.zs-tree-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    background: var(--color-white);
    border-radius: 4px;
}

.zs-tree-container.has-border {
    border: 1px solid var(--color-light-gray-border);
}

.zs-tree-header {
    padding: 12px;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-dark-blue);
    background-color: var(--color-very-light-gray-alt);
    border-bottom: 1px solid var(--color-light-gray-border);
}

.zs-tree {
    margin: 0;
    padding: 10px;
    overflow-y: auto;
    list-style: none;
}

.zs-tree-empty {
    padding: 20px;
    font-size: 0.9rem;
    font-style: italic;
    text-align: center;
    color: var(--color-gray-icon);
}

.zs-tree::-webkit-scrollbar {
    width: 6px;
}

.zs-tree::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.zs-tree::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
}

.zs-tree::-webkit-scrollbar-thumb:hover {
    background: #aaa;
}
</style>
